/**
 * Keyboard Shortcuts Hook - Phase 5.4 Enhanced
 *
 * Global keyboard shortcuts for the application
 *
 * Shortcuts:
 * - Cmd/Ctrl + K: Focus search / Open command palette
 * - Cmd/Ctrl + B: Toggle sidebar
 * - Cmd/Ctrl + S: Save form (in drawer/modal)
 * - Cmd/Ctrl + ,: Go to Settings
 * - ESC: Clear search / Close drawer / Close modal
 * - G then D: Go to Dashboard
 * - G then I: Go to Invoices
 * - G then P: Go to Payments
 * - G then A: Go to Accounting
 * - G then R: Go to Reports
 * - C then I: Create Invoice
 * - C then P: Record Payment
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import useNavigationStore from '../store/navigationStore';

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { setSearchQuery } = useNavigationStore();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields (except specific cases)
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      // Cmd/Ctrl + K: Command Palette (handled in MainLayout)
      // This shortcut is no longer handled here to avoid conflicts

      // Cmd/Ctrl + B: Toggle sidebar
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        // Trigger sidebar toggle via custom event
        window.dispatchEvent(new CustomEvent('toggle-sidebar'));
      }

      // Cmd/Ctrl + S: Save form (in drawer/modal)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        // Look for save/submit button in drawer or modal
        const saveButton = document.querySelector(
          '.ant-drawer-footer button[type="submit"], ' +
          '.ant-drawer-footer button.ant-btn-primary, ' +
          '.ant-modal-footer button[type="submit"], ' +
          '.ant-modal-footer button.ant-btn-primary'
        ) as HTMLButtonElement;
        if (saveButton && !saveButton.disabled) {
          saveButton.click();
        }
      }

      // ESC: Close drawer / modal / clear search
      if (e.key === 'Escape') {
        // Check if there's an open drawer
        const drawerClose = document.querySelector('.ant-drawer-close') as HTMLButtonElement;
        if (drawerClose) {
          drawerClose.click();
          return;
        }

        // Check if there's an open modal
        const modalClose = document.querySelector('.ant-modal-close') as HTMLButtonElement;
        if (modalClose) {
          modalClose.click();
          return;
        }

        // Otherwise clear search
        setSearchQuery('');
        // Blur search input if focused
        const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
        if (searchInput && document.activeElement === searchInput) {
          searchInput.blur();
        }
      }

      // Cmd/Ctrl + , (comma): Go to Settings
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        navigate('/settings/general');
      }

      // Skip letter shortcuts if typing in input
      if (isInput && !e.metaKey && !e.ctrlKey) {
        return;
      }

      // Go shortcuts (g + key)
      const now = Date.now();
      const lastG = (window as any).__lastGKeyPress || 0;
      const gPressed = now - lastG < 1000; // 1 second window

      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        (window as any).__lastGKeyPress = now;
      }

      if (gPressed && !e.metaKey && !e.ctrlKey && !e.altKey) {
        switch (e.key) {
          case 'd':
            e.preventDefault();
            navigate('/');
            delete (window as any).__lastGKeyPress;
            break;
          case 'i':
            e.preventDefault();
            navigate('/invoices');
            delete (window as any).__lastGKeyPress;
            break;
          case 'p':
            e.preventDefault();
            navigate('/payments/record');
            delete (window as any).__lastGKeyPress;
            break;
          case 'a':
            e.preventDefault();
            navigate('/accounting/chart-of-accounts');
            delete (window as any).__lastGKeyPress;
            break;
          case 'r':
            e.preventDefault();
            navigate('/reports');
            delete (window as any).__lastGKeyPress;
            break;
        }
      }

      // Create shortcuts (c + key)
      const lastC = (window as any).__lastCKeyPress || 0;
      const cPressed = now - lastC < 1000; // 1 second window

      if (e.key === 'c' && !e.metaKey && !e.ctrlKey && !e.altKey && !isInput) {
        (window as any).__lastCKeyPress = now;
      }

      if (cPressed && !e.metaKey && !e.ctrlKey && !e.altKey && !isInput) {
        switch (e.key) {
          case 'i':
            e.preventDefault();
            // Create Invoice - navigate to invoices with create action
            navigate('/invoices');
            // Wait for navigation, then trigger drawer
            setTimeout(() => {
              setSearchParams({ action: 'create' });
            }, 100);
            delete (window as any).__lastCKeyPress;
            break;
          case 'p':
            e.preventDefault();
            // Record Payment - navigate to payment recording
            navigate('/payments/record');
            delete (window as any).__lastCKeyPress;
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, setSearchQuery, setSearchParams]);
}
